import Pill from '@/app/components/ui/Pill';
import type { TargetPage } from '@/types/targetPage';
import type { Operator, QueryField } from './types';

type TargetPageFieldOptions = {
  name?: string;
  operators?: Operator[];
  matchTypeLabels?: Partial<Record<string, string>>;
  getOptionValue?: (targetPage: TargetPage) => string;
};

export const createTargetPageQueryField = (
  targetPages: TargetPage[],
  {
    name = 'url',
    operators = ['=', 'contains', 'beginsWith'],
    getOptionValue = targetPage => targetPage.url,
  }: TargetPageFieldOptions = {}
): QueryField => ({
  name,
  label: 'Page URL',
  type: 'select',
  autocomplete: true,
  operators,
  options: targetPages.map(targetPage => ({
    label: targetPage.url,
    value: getOptionValue(targetPage),
    meta: { targetPage },
  })),
  optionItem: option => {
    const targetPage = option.meta?.targetPage as TargetPage | undefined;
    if (!targetPage) return option.label;

    return (
      <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-medium text-foreground">{targetPage.url}</div>
          {targetPage.name && (
            <div className="truncate text-xs text-muted-foreground">{targetPage.name}</div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2 overflow-hidden">
          {targetPage.short_name && (
            <Pill
              variant="outline"
              color="purple"
              rounded={false}
              className="uppercase max-w-[140px] justify-start truncate"
            >
              <span className="truncate">{targetPage.short_name}</span>
            </Pill>
          )}
        </div>
      </div>
    );
  },
  tagItem: option => {
    const targetPage = option.meta?.targetPage as TargetPage | undefined;
    if (!targetPage) return option.label;

    return (
      <div className="flex min-w-0 items-center gap-2 overflow-hidden">
        {targetPage.short_name && (
          <Pill
            variant="outline"
            color="purple"
            rounded={false}
            className="uppercase max-w-[140px] justify-start truncate"
          >
            <span className="truncate">{targetPage.short_name}</span>
          </Pill>
        )}
      </div>
    );
  },
  defaultValue: '',
  placeholder: 'Type URL or select',
  children: [
    {
      name: 'visit_count',
      label: 'Visit count',
      type: 'number',
      operators: ['=', '>', '>=', '<', '<='],
      defaultValue: '',
      placeholder: 'Select an option',
    },
    {
      name: 'time_spent_seconds',
      label: 'Total time spent',
      type: 'duration',
      operators: ['>=', '<=', 'between'],
      defaultValue: '',
      placeholder: 'Select an option',
    },
  ],
});
